import React from 'react';
import PropTypes from 'prop-types';
import { ButtonBase, IconButton, Icon, CircularProgress, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

const useStyles = makeStyles(theme => ({
  button: {
    cursor: 'pointer',
    color: '#505050',
    whiteSpace: 'nowrap',
    padding: 0,
    border: 'none',
  },
  'color-uploaded': {
    color: theme.palette.primary.main,
  },
  'color-upload': {
    color: theme.palette.secondary.main,
  },
}));

const FileManager = ({ value = {}, onChange, onShowCertificate, id }) => {
  const {
    isPerformedWithSuccess,
    certificate,
    isFrozen = false,
    isMoodleCourse = false,
    id: valueId,
    isDownloading = false,
  } = value;
  const { t } = useTranslation('common');
  const classes = useStyles();
  const rowId = id ?? valueId;
  const hasCertificate = certificate !== null && certificate !== undefined;
  const shouldRenderDisabledDownload = !!isPerformedWithSuccess && isMoodleCourse && !hasCertificate;
  const certificateTooltip = t('trainingSessions.certificateDownloadTooltip');

  const button = classNames({
    [classes.button]: !isPerformedWithSuccess,
    [classes['color-uploaded']]: hasCertificate && !!isPerformedWithSuccess,
    [classes['color-upload']]: !hasCertificate && !!isPerformedWithSuccess && !shouldRenderDisabledDownload,
  });

  let buttonStatus;
  if (!isPerformedWithSuccess) {
    buttonStatus = t('students.certificateDisabled');
  } else if (isDownloading) {
    buttonStatus = <CircularProgress size={18} />;
  } else if (shouldRenderDisabledDownload) {
    buttonStatus = (
      <Tooltip title={certificateTooltip} placement="top" arrow>
        <span>
          <IconButton size="small" disabled>
            <Icon>cloud_download</Icon>
          </IconButton>
        </span>
      </Tooltip>
    );
  } else if (!!isPerformedWithSuccess && !hasCertificate) {
    buttonStatus = t('students.certificateUpload');
  } else {
    buttonStatus = (
      <Tooltip title={certificateTooltip} placement="top" arrow>
        <IconButton
          size="small"
          disabled={isDownloading}
          onClick={() => {
            onShowCertificate(rowId, certificate);
          }}
        >
          <Icon>cloud_download</Icon>
        </IconButton>
      </Tooltip>
    );
  }

  if (hasCertificate || shouldRenderDisabledDownload || isDownloading) {
    return buttonStatus;
  }
  return (
    <ButtonBase
      variant="contained"
      className={button}
      type="button"
      data-key={rowId}
      aria-label="state"
      onClick={() => onChange(rowId)}
      disabled={!isPerformedWithSuccess || isFrozen || isDownloading}
    >
      {buttonStatus}
    </ButtonBase>
  );
};

FileManager.defaultProps = {
  id: undefined,
};

FileManager.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.number,
    certificate: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.oneOf([null])]),
    isMoodleCourse: PropTypes.bool,
    isDownloading: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onShowCertificate: PropTypes.func.isRequired,
  id: PropTypes.number,
};

export default FileManager;
